import type { ItemInstance, PackingState } from "./schema";
import { normalizeReadinessState, assignItemToBag } from "./readiness";
import { batteryCheck, canSuggestBag } from "./cabin-checks";
import { FREE_RIGHTS, type Rights } from "./entitlement";

export type AssignmentGroup = {
	key: string;
	items: ItemInstance[];
	name: string;
	quantity: number;
	packed: number;
};
export function assignmentGroups(input: PackingState): AssignmentGroup[] {
	const state = normalizeReadinessState(input),
		groups = new Map<string, ItemInstance[]>();
	for (const item of state.items) {
		const key = JSON.stringify([
			item.presetId,
			item.name,
			item.recipientId,
			item.ownerId,
			item.assignedBagId,
			item.packMoment,
			item.weightG,
			item.batteryWh,
			item.liquidMl,
			item.tags,
		]);
		groups.set(key, [...(groups.get(key) ?? []), item]);
	}
	return [...groups].map(([key, items]) => ({
		key,
		items,
		name: items[0]!.name,
		quantity: items.length,
		packed: items.filter((item) => item.checklistStatus === "packed").length,
	}));
}
export const recipientLabel = (state: PackingState, item: ItemInstance) =>
	item.recipientId === "shared"
		? "함께 쓰는 것"
		: item.recipientId
			? `${state.trip.participants?.find((person) => person.id === item.recipientId)?.name ?? "주인 확인 필요"} 것`
			: "누구 것인지 미정";
export const packerLabel = (item: ItemInstance) =>
	!item.ownerId || item.ownerId === "shared" ? "챙길 사람 미정" : item.ownerId;
export const bagLabel = (state: PackingState, item: ItemInstance) =>
	state.bags.find((bag) => bag.id === item.assignedBagId)?.name ?? "가방 미정";
export function packedQuantity(
	input: PackingState,
	ids: string[],
	target: number,
): PackingState {
	const selected = input.items.filter((item) => ids.includes(item.id));
	if (!Number.isInteger(target) || target < 0 || target > selected.length)
		throw new Error("담은 수량은 0부터 전체 수량 사이로 확인해 주세요.");
	const ordered = [
		...selected.filter((item) => item.checklistStatus === "packed"),
		...selected.filter((item) => item.checklistStatus !== "packed"),
	];
	const packed = new Set(ordered.slice(0, target).map((item) => item.id));
	return {
		...input,
		items: input.items.map((item) =>
			ids.includes(item.id)
				? {
						...item,
						checklistStatus: packed.has(item.id)
							? "packed"
							: item.checklistStatus === "packed"
								? "ready"
								: item.checklistStatus,
					}
				: item,
		),
	};
}
export type AssignmentPatch = {
	name: string;
	recipientId?: string;
	ownerId: string;
	bagId?: string;
	quantity: number;
	last: boolean;
};
export function updateAssignment(
	input: PackingState,
	ids: string[],
	patch: AssignmentPatch,
	rights: Rights = FREE_RIGHTS,
): PackingState {
	const selected = input.items.filter((item) => ids.includes(item.id)),
		template = selected[0];
	if (!template || selected.length !== ids.length)
		throw new Error("목록이 바뀌었어요. 물건을 다시 열어 주세요.");
	if (
		!patch.name.trim() ||
		patch.name.trim().length > 80 ||
		!Number.isInteger(patch.quantity) ||
		patch.quantity < 1 ||
		patch.quantity > Math.max(30, selected.length)
	)
		throw new Error("물건 이름과 수량 1~30개를 확인해 주세요.");
	if (
		patch.quantity <
		selected.filter((item) => item.checklistStatus === "packed").length
	)
		throw new Error("이미 담은 수량보다 줄이려면 먼저 담은 수량을 빼 주세요.");
	if (
		patch.recipientId &&
		patch.recipientId !== "shared" &&
		!input.trip.participants?.some((person) => person.id === patch.recipientId)
	)
		throw new Error("누구의 물건인지 다시 골라 주세요.");
	if (
		patch.ownerId !== "shared" &&
		!input.trip.ownerIds.includes(patch.ownerId)
	)
		throw new Error("챙길 사람을 다시 골라 주세요.");
	if (
		input.items.length - selected.length + patch.quantity >
		Math.max(rights.maxItemsPerTrip, input.items.length)
	)
		throw new Error("여행당 준비물 한도를 넘어요. 기존 물건은 그대로예요.");
	const keep = [
		...selected.filter((item) => item.checklistStatus === "packed"),
		...selected.filter((item) => item.checklistStatus !== "packed"),
	].slice(0, patch.quantity);
	const removed = new Set(
		selected
			.filter((item) => !keep.some((kept) => kept.id === item.id))
			.map((item) => item.id),
	);
	const additions = Array.from(
		{ length: Math.max(0, patch.quantity - selected.length) },
		(_, index): ItemInstance => ({
			...template,
			id: `${input.trip.id}:item-${crypto.randomUUID()}`,
			quantityIndex:
				Math.max(0, ...input.items.map((item) => item.quantityIndex)) +
				index +
				1,
			checklistStatus: "planned",
			placementId: undefined,
		}),
	);
	const targetIds = new Set([...keep, ...additions].map((item) => item.id));
	let state: PackingState = {
		...input,
		items: [
			...input.items.filter((item) => !removed.has(item.id)),
			...additions,
		].map((item) =>
			targetIds.has(item.id)
				? {
						...item,
						name: patch.name.trim(),
						recipientId: patch.recipientId,
						ownerId: patch.ownerId,
						packMoment: patch.last ? "departure_morning" : "anytime",
					}
				: item,
		),
		placements: input.placements.filter(
			(value) => !removed.has(value.itemInstanceId),
		),
		trip: {
			...input.trip,
			itemInstanceIds: input.trip.itemInstanceIds
				.filter((id) => !removed.has(id))
				.concat(additions.map((item) => item.id)),
			extractionOrder: input.trip.extractionOrder.filter(
				(id) => !removed.has(id),
			),
		},
	};
	for (const id of targetIds)
		if (
			state.items.find((item) => item.id === id)?.assignedBagId !== patch.bagId
		)
			state = assignItemToBag(state, id, patch.bagId);
	return state;
}
export function packingProblems(state: PackingState) {
	const battery = batteryCheck(state);
	return {
		battery,
		unbagged: state.items.filter((item) => !item.assignedBagId),
		unassigned: state.items.filter(
			(item) => item.ownerId === "shared" || !item.ownerId,
		),
		unknownWeights: state.items.filter((item) => item.weightG === undefined),
		cabinBags: state.bags.filter(
			(bag) =>
				(bag.role === "cabin" || bag.role === "personal") &&
				battery.inChecked.every((item) => canSuggestBag(item, bag)),
		),
	};
}
export function remainingAssignmentText(state: PackingState, who = "all") {
	const groups = assignmentGroups(state).filter(
			(group) => who === "all" || group.items[0]!.ownerId === who,
		),
		problems = packingProblems(state);
	const rows = groups
		.filter((group) => group.packed < group.quantity)
		.map((group) => {
			const item = group.items[0]!;
			return `□ ${recipientLabel(state, item)} ${group.name} ${group.quantity - group.packed}개 · ${packerLabel(item)} · ${bagLabel(state, item)}${item.packMoment === "departure_morning" ? " · 출발 아침" : ""}`;
		});
	const selected = state.items.filter(
		(item) => who === "all" || item.ownerId === who,
	);
	const issues = [
		...problems.battery.inChecked
			.filter((item) => selected.includes(item))
			.map(
				(item) =>
					`△ ${item.name}: 기내 가방으로 배정하고 실제 이동을 확인해 주세요.`,
			),
		...problems.unbagged
			.filter((item) => selected.includes(item))
			.map((item) => `△ ${item.name}: 가방 미정`),
	];
	return [
		state.trip.title,
		who === "all"
			? "전체 남은 준비"
			: who === "shared"
				? "챙길 사람 미정"
				: `${who}님이 챙길 것`,
		...rows,
		...issues,
		rows.length ? "" : "남은 물건 없음",
		`무게 미측정 ${selected.filter((item) => item.weightG === undefined).length}개 · 항공 반입 허가가 아닙니다.`,
		"복사·저장은 실제 발송·읽음·공동 수정이 아닙니다.",
	]
		.filter(Boolean)
		.join("\n");
}
export function bagManifestText(state: PackingState) {
	const groups = assignmentGroups(state);
	return [
		state.trip.title,
		...[
			...state.bags.map((bag) => ({ id: bag.id, name: bag.name })),
			{ id: undefined, name: "가방 미정" },
		].map((bag) =>
			[
				bag.name,
				...groups
					.filter((group) => group.items[0]!.assignedBagId === bag.id)
					.map(
						(group) =>
							`${recipientLabel(state, group.items[0]!)} · ${group.name} ${group.packed}/${group.quantity}개 · ${packerLabel(group.items[0]!)}`,
					),
			].join("\n"),
		),
		`무게 미측정 ${state.items.filter((item) => item.weightG === undefined).length}개 · 알려지지 않은 무게는 0이 아닙니다.`,
	].join("\n\n");
}
